import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getCurrentDate,
  throwOnError,
  useAuth,
  useGetPlansQuery
} from 'prove-common'
import {
  buildTaskOverviewRows,
  type PlanOverviewStatus,
  type TaskOverviewRow
} from '../utils/tasksTableUtils'

const TASKS_STALE_TIME = 5 * 60 * 1000

const TASK_OVERVIEW_STATUSES: PlanOverviewStatus[] = [
  'draft',
  'active',
  'deactivated'
]

/**
 * Loads every task the overview screens care about. The endpoint has no
 * workspace filter, so scoping happens client side. The query key is shared by
 * the tasks, plans and tilsynsdivisjon screens so they reuse one cache entry
 * instead of each triggering their own request.
 */
export const useGetPlanTasksQuery = () => {
  const { authFetch } = useAuth()

  return useQuery({
    queryKey: ['tasks', 'proveplan-overview'],
    staleTime: TASKS_STALE_TIME,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/tasks', {
        params: {
          query: {
            status: TASK_OVERVIEW_STATUSES
          }
        }
      })

      if (error) throwOnError(error)

      return data
    }
  })
}

export type TaskOverviewRowsResult = {
  rows: TaskOverviewRow[]
  today: string
  /** True until plans are available. Screens need these to render at all. */
  isPendingPlans: boolean
  /** True until tasks are available; screens show an inline spinner for this. */
  isPendingTasks: boolean
  isPending: boolean
  isFetching: boolean
  isError: boolean
}

/**
 * Combines plans and tasks into the denormalised rows used by the task
 * overview, the workspace summary card and the tilsynsdivisjon overview.
 */
export const useTaskOverviewRows = (): TaskOverviewRowsResult => {
  const today = getCurrentDate()
  const {
    data: plansData,
    isPending: isPendingPlans,
    isFetching: isFetchingPlans,
    isError: isErrorPlans
  } = useGetPlansQuery()
  const {
    data: tasks,
    isPending: isPendingTasks,
    isFetching: isFetchingTasks,
    isError: isErrorTasks
  } = useGetPlanTasksQuery()

  const plans = useMemo(() => plansData?.plans ?? [], [plansData?.plans])
  const planById = useMemo(
    () => new Map(plans.map((plan) => [plan.id, plan])),
    [plans]
  )
  const rows = useMemo(
    () => buildTaskOverviewRows(tasks, planById, today),
    [planById, tasks, today]
  )

  return {
    rows,
    today,
    isPendingPlans,
    isPendingTasks,
    isPending: isPendingPlans || isPendingTasks,
    isFetching: isFetchingPlans || isFetchingTasks,
    isError: isErrorPlans || isErrorTasks
  }
}
